import { nextTick, onBeforeUnmount, onMounted, onUpdated, ref, shallowRef, type Ref } from 'vue'

export function chipParts(chip: HTMLElement) {
  const spans = chip.querySelectorAll('span')
  const last = spans[spans.length - 1]?.textContent?.trim() ?? ''
  const count = /^[\d.,\s%+\u2212-]+$/.test(last) ? last : ''
  const text = chip.textContent?.replace(/\s+/g, ' ').trim() ?? ''
  return { label: (count && text.endsWith(count) ? text.slice(0, -count.length).trim() : text) || chip.title, count }
}

function activeIndices(chips: HTMLElement[]) {
  let explicit = false
  for (let index = 0; index < chips.length; index++) {
    const pressed = chips[index]!.getAttribute('aria-pressed') ?? chips[index]!.getAttribute('aria-selected')
    if (pressed !== null) { explicit = true; if (pressed === 'true') return [index] }
  }
  if (explicit) return []
  // Prototype L57–68 also supports legacy chips selected only by their background.
  const backgrounds = chips.map(chip => getComputedStyle(chip).backgroundColor)
  const majority = backgrounds.reduce((best, color) => backgrounds.filter(item => item === color).length > backgrounds.filter(item => item === best).length ? color : best, '')
  const index = backgrounds.findIndex(color => color !== majority && !clearColor(color))
  return index < 0 ? [] : [index]
}
function clearColor(color: string) { return !color || color === 'transparent' || /rgba\([^)]*,\s*0\)$/.test(color) }

export function useChipRow(row: Ref<HTMLElement | undefined>, more: Ref<HTMLButtonElement | undefined>, menu: Ref<HTMLElement | undefined>) {
  const hidden = shallowRef<HTMLElement[]>([])
  const open = ref(false)
  const menuTarget = shallowRef<HTMLElement | string>('body')
  let observer: ResizeObserver | undefined
  let mutations: MutationObserver | undefined
  let styles: MutationObserver | undefined
  let theme: MutationObserver | undefined
  let frame = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let pending = false
  let disposed = false

  function close(restoreFocus = false) {
    open.value = false
    if (restoreFocus) more.value?.focus()
  }
  function schedule() {
    if (pending || disposed) return
    pending = true
    const run = () => {
      if (!pending) return
      pending = false; cancelAnimationFrame(frame); clearTimeout(timer); fit()
    }
    // Prototype L54–55: background tabs still measure via the timer fallback.
    frame = requestAnimationFrame(run); timer = setTimeout(run, 32)
  }
  function styleMore(chips: HTMLElement[], active: number[]) {
    const reference = chips.find((_, index) => !active.includes(index)) ?? chips[0]
    if (!reference || !more.value) return
    const style = getComputedStyle(reference)
    Object.assign(more.value.style, {
      height: style.height, padding: `0 ${style.paddingRight}`, borderRadius: style.borderTopLeftRadius,
      border: `${style.borderTopWidth} ${style.borderTopStyle} ${style.borderTopColor}`,
      color: style.color, fontFamily: style.fontFamily, fontSize: style.fontSize, fontWeight: style.fontWeight,
    })
  }
  function fit() {
    if (!row.value || !more.value) return
    const chips = Array.from(row.value.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child !== more.value)
    styles?.disconnect()
    for (const chip of chips) {
      styles?.observe(chip, { attributes: true, attributeFilter: ['style'] })
      chip.removeAttribute('data-fw-ov')
    }
    const active = activeIndices(chips)
    styleMore(chips, active)
    more.value.hidden = false
    const style = getComputedStyle(row.value), gap = parseFloat(style.columnGap) || 8
    const moreWidth = more.value.getBoundingClientRect().width
    const widths = chips.map(chip => chip.getBoundingClientRect().width)
    const total = widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, chips.length - 1)
    // Prototype L81–113: stable minimum, active reservation, then source-order prefix.
    const minimum = Math.ceil(Math.min(total, Math.max(0, ...widths) + gap + moreWidth))
    row.value.style.minWidth = `${minimum}px`
    const available = row.value.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0)
    const overflow: HTMLElement[] = []
    if (total > available + .5 && chips.length > 1) {
      const keep = new Set(active)
      let used = active.reduce((sum, index) => sum + widths[index]! + gap, 0)
      for (let index = 0; index < chips.length; index++) {
        if (keep.has(index)) continue
        if (used + widths[index]! + gap > available - moreWidth) break
        keep.add(index); used += widths[index]! + gap
      }
      chips.forEach((chip, index) => { if (!keep.has(index)) { chip.setAttribute('data-fw-ov', '1'); overflow.push(chip) } })
    }
    const changed = overflow.length !== hidden.value.length || overflow.some((chip, index) => chip !== hidden.value[index])
    if (changed) { hidden.value = overflow; close() }
    more.value.hidden = overflow.length === 0
    const title = overflow.map(chip => { const part = chipParts(chip); return `${part.label}${part.count ? ` ${part.count}` : ''}` }).join(' · ')
    if (more.value.title !== title) more.value.title = title
  }
  function background() {
    // Prototype L115–119: callers can explicitly select their surface colour.
    const explicit = getComputedStyle(row.value!).getPropertyValue('--fw-chip-menu-background').trim()
    if (explicit) return explicit
    for (let element = row.value?.parentElement; element; element = element.parentElement) {
      const color = getComputedStyle(element).backgroundColor
      if (!clearColor(color)) return color
    }
    return getComputedStyle(row.value!).getPropertyValue('--fw-bg-surface').trim()
  }
  // Prototype L120–176: fixed menu placement, keyboard navigation and dismissal.
  async function show() {
    if (!hidden.value.length || !row.value || !more.value) return
    // Keep nested menus in the modal's top layer and focus boundary.
    menuTarget.value = row.value.closest<HTMLElement>('dialog, [role="dialog"], [role="alertdialog"]') ?? 'body'
    open.value = true
    await nextTick()
    if (!menu.value || !open.value) return
    const style = getComputedStyle(row.value), reference = getComputedStyle(more.value)
    Object.assign(menu.value.style, { background: background(), borderColor: reference.borderTopColor, color: style.color, fontFamily: reference.fontFamily })
    const anchor = more.value.getBoundingClientRect(), popup = menu.value
    popup.style.left = `${Math.max(8, Math.min(anchor.left, window.innerWidth - popup.offsetWidth - 8))}px`
    if (window.innerHeight - anchor.bottom < popup.offsetHeight + 12 && anchor.top > window.innerHeight - anchor.bottom) popup.style.bottom = `${window.innerHeight - anchor.top + 6}px`
    else popup.style.top = `${anchor.bottom + 6}px`
    popup.querySelector('button')?.focus()
  }
  function keydown(event: KeyboardEvent) {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true) }
    else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const items = Array.from(menu.value?.querySelectorAll('button') ?? [])
      const index = items.indexOf(document.activeElement as HTMLButtonElement)
      items[(index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus()
    } else if (event.key === 'Tab') close()
  }
  function activate(chip: HTMLElement) {
    if (chip.getAttribute('aria-disabled') === 'true' || (chip instanceof HTMLButtonElement && chip.disabled)) return
    close(); chip.click(); more.value?.focus(); schedule()
  }
  function outside(event: Event) { if (!menu.value?.contains(event.target as Node) && !event.composedPath().includes(more.value!)) close() }
  function scroll(event: Event) { if (!(event.target instanceof Node) || !menu.value?.contains(event.target)) close() }
  const resize = () => close()
  onMounted(() => {
    observer = new ResizeObserver(schedule); observer.observe(row.value!)
    mutations = new MutationObserver(schedule)
    mutations.observe(row.value!, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'aria-pressed', 'aria-selected', 'title', 'disabled', 'aria-disabled'] })
    styles = new MutationObserver(schedule)
    theme = new MutationObserver(() => { close(); schedule() })
    for (let ancestor = row.value?.parentElement; ancestor; ancestor = ancestor.parentElement) theme.observe(ancestor, { attributes: true, attributeFilter: ['class', 'style', 'data-theme'] })
    document.fonts?.ready.then(schedule)
    document.addEventListener('visibilitychange', schedule)
    document.addEventListener('pointerdown', outside, true)
    window.addEventListener('scroll', scroll, true); window.addEventListener('resize', resize)
    fit(); schedule()
  })
  onUpdated(schedule)
  onBeforeUnmount(() => {
    disposed = true; observer?.disconnect(); mutations?.disconnect(); styles?.disconnect(); theme?.disconnect()
    cancelAnimationFrame(frame); clearTimeout(timer); close()
    document.removeEventListener('visibilitychange', schedule)
    document.removeEventListener('pointerdown', outside, true)
    window.removeEventListener('scroll', scroll, true); window.removeEventListener('resize', resize)
  })
  return { hidden, open, menuTarget, show, close, keydown, activate }
}
